import { AccountInfo } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { loginRequest } from "../services/authConfig";

interface AuthContextValue {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  allAccounts: AccountInfo[];
  login: () => Promise<void>;
  logout: (account?: AccountInfo) => Promise<void>;
  switchAccount: (account: AccountInfo) => void;
  addAccount: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [loading, setLoading] = useState(true);
  // Un ref, pas un état : cet effet le lit et l'écrit lui-même. En dépendance,
  // il rejouerait l'effet qui vient de l'écrire ; hors dépendances, il mentirait
  // sur ce que l'effet regarde vraiment.
  const previousAccountsLength = useRef(0);

  // Le compte actif appartient à MSAL, qui le garde hors de React et ne
  // prévient personne quand il change. Cet effet est l'abonnement à ce
  // magasin extérieur : il ne peut que le lire, le corriger, puis recopier
  // le résultat dans un état. Le calculer pendant le rendu lirait une valeur
  // que React ne contrôle pas, et déclencherait `setActiveAccount` — un effet
  // de bord — au milieu d'un rendu.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    // Déterminer le compte actif
    if (accounts.length > 0) {
      // Vérifier si un nouveau compte a été ajouté
      const newAccountAdded = accounts.length > previousAccountsLength.current;

      if (newAccountAdded) {
        // Basculer automatiquement vers le dernier compte ajouté
        const newAccount = accounts[accounts.length - 1];
        instance.setActiveAccount(newAccount);
        setUser(newAccount);
      } else {
        // Comportement normal : utiliser le compte actif ou le premier
        const activeAccount = instance.getActiveAccount();
        if (activeAccount) {
          setUser(activeAccount);
        } else {
          const account = accounts[0];
          instance.setActiveAccount(account);
          setUser(account);
        }
      }

      // Mettre à jour le nombre de comptes précédents
      previousAccountsLength.current = accounts.length;
    } else {
      setUser(null);
      previousAccountsLength.current = 0;
    }
    setLoading(false);
  }, [accounts, instance]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const logout = async (account?: AccountInfo) => {
    try {
      const logoutAccount = account || instance.getActiveAccount();

      await instance.logoutRedirect({
        account: logoutAccount || undefined,
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const switchAccount = (account: AccountInfo) => {
    instance.setActiveAccount(account);
    setUser(account);
  };

  const addAccount = async () => {
    try {
      // Force l'affichage du sélecteur de compte
      await instance.loginRedirect({
        ...loginRequest,
        prompt: "select_account",
      });
    } catch (error) {
      console.error("Add account failed:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        // Les comptes viennent de MSAL tels quels : les recopier dans un état
        // n'ajoutait qu'un rendu et une occasion de diverger.
        allAccounts: accounts,
        login,
        logout,
        switchAccount,
        addAccount,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
